<template>
  <div>
    <p>Please select <strong>ONE</strong> of the following methods to search by</p>
    <div class="form-group row">
      <div class="col-md-6">
        <el-input
          type="search"
          clearable
          @keyup.enter.native="fetchResults"
          placeholder="Search for form named..."
          v-model="searchQuery" />
      </div>
      <div class="col-md-6">
        <v-select
          v-model="taskName"
          :value="taskName"
          placeholder="Search by Task Name"
          @input="setTaskName"
          label="label"
          code="value"
          :options="taskNamesForSelect">
        </v-select>
      </div>
      <div class="col-md-12">
        <button type="button" @click="showUnassociatedInspectionForms" style="float: right" class="btn btn-fill btn-info">
          Unassociated Files
        </button>
        <button type="button" @click="fetchResults" style="float: right" class="btn btn-fill btn-info">
          Search
        </button>
        <button type="button" @click="clearResults" style="float: right" class="btn btn-fill btn-info">
          Clear
        </button>
        <button type="button" @click="fetchAll" style="float: right" class="btn btn-fill btn-info">
          View All
        </button>
      </div>
    </div>
    <el-table class="table-striped indexed-table-column-width"
              :data="tableData"
              border
              style="width: 100%"
              :max-height="tableHeight(300)">
      <el-table-column
        :width="indexColumnWidth"
        align="center"
        label="#"
        type="index">
      </el-table-column>
      <el-table-column
        label="Name"
        sortable
        :sort-method="(a, b) => callStringSort(a, b, 'name')"
        min-width="200">
        <template v-slot="props">
          <span class="invisible">{{ props.row.id }}</span>
          <a href="#" @click.prevent="showFormDetails(props.row)">{{ props.row.name }}</a>
        </template>
      </el-table-column>
      <el-table-column
        label="Uploaded By"
        sortable
        :sort-method="(a, b) => callStringSort(a, b, 'uploadedByLabel')"
        min-width="125">
        <template v-slot="props">
          {{ props.row.uploadedByLabel }}
        </template>
      </el-table-column>
      <el-table-column
        label="Last Used"
        sortable
        :sort-method="(a, b) => callDateSort(a, b, 'lastUsedAt')"
        min-width="125">
        <template v-slot="props">
          {{ props.row.lastUsedAt | formatDate }}
        </template>
      </el-table-column>
      <el-table-column
        width="140"
        fixed="right"
        label="Actions">
        <template v-slot="props">
          <p-button type="success" size="sm" icon class="btn-left">
            <i class="fa fa-info" @click="showFormDetails(props.row)"></i>
          </p-button>
          <p-button type="info" size="sm" icon @click="downloadFile(props.row)">
            <i class="fa fa-download"></i>
          </p-button>
          <p-button type="danger" size="sm" icon v-if="canEdit" @click="confirmDelete(props.row)">
            <i class="fa fa-times"></i>
          </p-button>
        </template>
      </el-table-column>
    </el-table>
    <div class="col-sm-6 pagination-info" style="text-align: left;">
      <span class="category">Showing {{total}} entries</span>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import Constants from 'src/utilities/constants'
import PButton from 'src/components/UIComponents/Button.vue'
import {Card, TabPane, Tabs} from 'src/components/UIComponents'
import {
  dateSort,
  getTableHeight,
  notifyError,
  notifySuccess,
  notifyWarning,
  setIndexColumnWidth,
  stringSort
} from 'src/utilities/utils'
import {NO_CONTENT, NOT_FOUND, OK, UNAUTHORIZED} from 'http-status-codes'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

  export default{
    components: {
      Card,
      PButton,
      Tabs,
      TabPane,
      vSelect
    },
    computed: {
      ...mapGetters({
        taskNamesForSelect: 'tasks/names/kvpsForSelect',
        tableData: 'files/inspectionForms/results'
      }),
      tableData() {
        return this.$store.get('files/inspectionForms@results')
      },
      total() {
        return this.tableData.length
      },
      canSearch() {
        return this.$can('read', 'filesSearch')
      },
      canEdit() {
        return this.$can('edit', 'files')
      },
      indexColumnWidth() {
        return setIndexColumnWidth(this.tableData.length)
      }
    },
    data() {
      return {
        toDelete: null,
        taskName: null,
        searchQuery: '',
        constants: Constants
      }
    },
    methods: {
      downloadFile(file) {
        this.$store.dispatch('files/download', {
          useDirectFileUploadAndDownload: true,
          name: file.name,
          path: file.path
        })
      },
      getUserDisplayName(userId) {
        return this.$store.getters['users/displayName'](userId)
      },
      setTaskName(taskName) {
        this.$store.set('tasks/search/query@nameId', null)
        if (taskName) {
          this.$store.set('tasks/search/query@nameId', taskName.value)
        }
      },
      showFormDetails(file) {
        this.$eventHub.$emit('show-modal-1', 'File Details', file, '600px')
      },
      async confirmDelete(file) {
        const response = await this.$store.dispatch('tasks/names/fileInUse', file)

        if (OK !== response.status) {
          notifyError(this, 'Error determining if ' + file.name + ' is in use. Unable to delete form.')
          return
        }

        let message
        let showButton = true
        const usedByTaskNames = response.data

        if (usedByTaskNames.length === 0) {
          message = 'Are you sure you want to delete <strong>' + file.name + '</strong>? This will completely remove the form.'
        } else {
          message = '<strong>' + file.name + '</strong> is used by the following task names and cannot be deleted:'
          message += '<p><ul>'
          usedByTaskNames.forEach((taskName) => {
            message += '<li>' + taskName.name + '</li>'
          })
          message += '</ul></p>'
          message += '<p>If you really want to delete this file, you must first remove associations to it in each of the task names listed above, then come back here and delete the file.</p>'
          showButton = false
        }

        this.toDelete = file
        const payload = {
          message,
          entity: file,
          delete: this.delete,
          showButton,
        }
        this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
      },
      async delete() {
        const response = await this.$store.dispatch('files/inspectionForms/deleteOne', this.toDelete)
        if (NO_CONTENT === response.status) {
          notifySuccess(this, 'Form Deleted Successfully')
        } else if (NOT_FOUND === response.status) {
          notifyWarning(this, 'Unable to delete file. Inspection Form not found.')
        } else if (UNAUTHORIZED === response.status) {
          notifyWarning(this, 'You are not authorized to delete Inspection Forms')
        } else {
          notifyError(this, 'Error Deleting File')
        }
      },
      callStringSort(a, b, prop) {
        return stringSort(a, b, prop)
      },
      callDateSort(a, b, prop) {
        return dateSort(a, b, prop)
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      async fetchResults() {
        let response
        if (this.taskName && this.taskName.value) {
          response = await this.$store.dispatch('files/inspectionForms/fetchResultsByTaskNameId', this.taskName.value)
        } else {
          response = await this.$store.dispatch('files/inspectionForms/fetchResults', this.searchQuery)
        }
        // Display No Results Found if no data in response, or, when there is results that contain no files. Response.data[0] only returns when there are files.
        if (OK === response.status && response.data.length === 0 || (!response.data[0] && response.data.files.length === 0)) {
          this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
        }
      },
      async fetchAll() {
        const response = await this.$store.dispatch('files/inspectionForms/fetchAll')
        if (OK !== response.status) {
          notifyError(this, 'Failed to load all inspection forms')
        }
      },
      clearResults() {
        this.taskNameId = null
        this.taskName = null
        this.searchQuery = null
        this.$store.commit('files/inspectionForms/resetResults')
      },
      showUnassociatedInspectionForms() {
        this.$eventHub.$emit('show-modal-1', 'Unassociated Inspection Forms', null, '1200px')
      }
    },
    beforeMount() {
      this.$store.cache.dispatch('tasks/names/fetchAll')
    },
    destroyed() {
      this.clearResults()
    }
  }
</script>
